import { io, type Socket } from 'socket.io-client'
import { websocketConfig, channels, type ChannelType } from '@/config/websocket.config'
import { RT, RT_CLIENT, RT_LOCAL } from '@/services/realtime-events'
import { useAuthStore } from '@/stores/auth'
/**
 * P13-V7B — the router is reached lazily. A static `import router from '@/router'` here closed the
 * cycle router/index → guards → stores/auth → websocket.service → router/index. Whenever this module was
 * evaluated inside that chain (guards still initialising), the router's top-level route table read
 * `AGENDA_PILOT_SURFACE_ROLES` before `guards.ts` had defined it and the application died on a blank
 * page — "Cannot access 'AGENDA_PILOT_SURFACE_ROLES' before initialization", reproduced by the V7B
 * browser composition (J2c). The router is only needed at run time, to leave for /login.
 */
const goToLogin = (): void => {
  import('@/router')
    .then(({ default: router }) => router.push('/login'))
    .catch(() => {})
}
import NotificationService from '@/services/notification.service'
import { isFrontendTestMode } from '@/config/runtimeConfig'

export interface WebSocketEventCallback {
  (data: any): void
}

export interface SubscriptionOptions {
  filters?: Record<string, any>
  queueMessages?: boolean
}

class WebSocketService {
  private socket: Socket | null = null
  private isConnected = false
  private isConnecting = false
  private isReconnecting = false
  private eventListeners: Map<string, WebSocketEventCallback[]> = new Map()
  private subscriptions: Set<string> = new Set()
  private messageQueue: Array<{ event: string; data: any }> = []
  private connectionPromise: Promise<void> | null = null
  private throttleWindowMs = 250
  private paused = false
  private pendingBatch: Record<string, any[]> = {}
  private batchTimer: any = null

  // 🚀 Conexión Principal
  async connect(): Promise<void> {
    // Evitar múltiples conexiones simultáneas
    if (this.connectionPromise) {
      return this.connectionPromise
    }

    if (this.isConnected || this.isConnecting) {
      console.debug('WebSocket already connected or connecting')
      return Promise.resolve()
    }

    this.connectionPromise = this._performConnection()
    return this.connectionPromise
  }

  private async _performConnection(): Promise<void> {
    const authStore = useAuthStore()
    
    const resolvedToken = authStore.token || (isFrontendTestMode ? 'test-token-LOCAL' : null)

    if (!resolvedToken) {
      throw new Error('No authentication token available')
    }

    this.isConnecting = true

    return new Promise((resolve, reject) => {
      try {
        this.socket = io(websocketConfig.url, {
          path: websocketConfig.path,
          auth: {
            token: resolvedToken
          },
          autoConnect: websocketConfig.autoConnect,
          reconnection: websocketConfig.reconnection,
          reconnectionAttempts: websocketConfig.reconnectionAttempts,
          reconnectionDelay: websocketConfig.reconnectionDelay,
          timeout: websocketConfig.timeout,
          transports: websocketConfig.transports
        })

        this.setupEventListeners()

        // P13-V1D BOUNDED REPAIR R1 (F5 — SOCKET_RECONNECT_SIGNAL_NOT_REAL): la instancia `Socket`
        // JAMÁS emite 'reconnect' (ese lifecycle vive en el Manager `socket.io`); registrarlo ahí
        // era un bridge muerto — una reconexión real no producía resync. La ÚNICA autoridad del
        // reconnect local es el SEGUNDO 'connect' exitoso del MISMO socket: Socket.IO reemite
        // 'connect' en cada recuperación, así que cada reconexión física produce EXACTAMENTE una
        // señal local; la conexión INICIAL (flag per-instancia) jamás cuenta como reconnect y una
        // instancia nueva (disconnect() manual → connect()) empieza de cero.
        let hasConnectedOnce = false
        // P13-V1D R2 (B3): un `connect_error` inicial seguido de una recuperación automática del
        // Manager produce un `connect` que ES una recuperación real (el inbox pudo montar y leer
        // durante el outage) — sin esta pieza, `isRecovery` quedaba false y el resync jamás
        // ocurría. CONNECT_ERROR → LATER CONNECT_SUCCESS ⇒ EXACTLY ONE SOCKET_RECONNECT.
        let hadConnectionError = false

        this.socket.on('connect', () => {
          const isRecovery = hasConnectedOnce || hadConnectionError
          hasConnectedOnce = true
          hadConnectionError = false
          console.log('✅ WebSocket connected successfully')
          this.isConnected = true
          this.isConnecting = false
          this.isReconnecting = false
          this.connectionPromise = null

          // Procesar cola de mensajes
          this.processMessageQueue()

          // Emitir evento local para listeners
          this.emit(RT_LOCAL.SOCKET_CONNECT, { id: this.socket?.id })
          if (isRecovery) {
            this.emit(RT_LOCAL.SOCKET_RECONNECT, { recovered: true, id: this.socket?.id })
          }
          resolve()
        })

        this.socket.on('connect_error', (error) => {
          console.error('❌ WebSocket connection error:', error)
          hadConnectionError = true // R2 (B3): el próximo connect exitoso es una RECUPERACIÓN
          this.isConnecting = false
          this.connectionPromise = null
          this.emit(RT_LOCAL.SOCKET_CONNECT_ERROR, error)
          reject(error)
        })

        this.socket.connect()

      } catch (error) {
        this.isConnecting = false
        this.connectionPromise = null
        reject(error)
      }
    })
  }

  // 🎧 Event Listeners Setup
  // P13-K4A: the retired inventory / route / agenda / realtime:*_update / system-bulletin bridges are
  // GONE. Each is a RETIRED record in the backend catalog with no producer, so bridging them would
  // have advertised a channel that can never carry anything.
  private setupEventListeners(): void {
    if (!this.socket) return

    // Eventos de conexión
    this.socket.on('disconnect', (reason) => {
  console.debug('🔌 WebSocket disconnected:', reason)
      this.isConnected = false
      this.connectionPromise = null
      this.emit(RT_LOCAL.SOCKET_DISCONNECT, reason)
    })

    // P13-V1D R1 (F5): el bridge `socket.on('reconnect')` fue RETIRADO — la instancia Socket no
    // emite ese evento (superficie muerta). La señal local SOCKET_RECONNECT nace EXCLUSIVAMENTE
    // del second-connect real (ver connect()); no se puentea el Manager para evitar doble emisión.
    // Los ERRORES de reintento sí viven en el Manager: única superficie que los emite.
    this.socket.io?.on?.('reconnect_error', (error) => {
      console.error('🔄❌ WebSocket reconnection error:', error)
      this.isReconnecting = true
      this.emit(RT_LOCAL.SOCKET_RECONNECT_ERROR, error)
    })

    // Eventos del backend - Conectar con websocketServer.js existente
    this.socket.on(RT.CONNECTION_ESTABLISHED, (data) => {
  console.debug('🏠 Connection established:', data)
    })

    // PR-08: Respuesta de subscribe:rooms validado por backend
    this.socket.on(RT.SUBSCRIBE_ROOMS_RESULT, (data: any) => {
      if (data.rejected?.length > 0) {
        console.warn('⚠️ Rooms rechazadas por backend:', data.rejected)
      }
      if (data.joined?.length > 0) {
        console.debug('✅ Rooms confirmadas:', data.joined)
      }
    })

    // Eventos de tiempo real - Ya implementados en backend
    // 🔄 Incidencias: Cambio de estado específico (SIN THROTTLING para inmediatez)
    this.socket.on(RT.INCIDENT_STATUS_CHANGED, (data) => {
      console.log('📡 [WEBSOCKET_SERVICE] Evento incident:status-changed recibido del backend:', data)
      // Emitir inmediatamente sin throttling para actualización en tiempo real
      this.emit(RT_LOCAL.INCIDENT_STATUS_CHANGED, data)
    })

    // P13-V6B@V1 — señales de dominio Incidencias (SIN THROTTLING): el payload es una señal para
    // refetch canónico server-scoped, nunca materializa estado ni autoriza nada.
    this.socket.on(RT.INCIDENT_CREATED, (data) => {
      this.emit(RT_LOCAL.INCIDENT_CREATED, data)
    })
    this.socket.on(RT.INCIDENT_DEPARTMENT_ASSIGNED, (data) => {
      this.emit(RT_LOCAL.INCIDENT_DEPARTMENT_ASSIGNED, data)
    })
    // P13-V1C — autoridad del claim atómico (CLAIMED | ASSIGNED | RELEASED). Señal-solo: el
    // consumidor refetchea la autoridad persistida; el payload jamás se materializa como estado.
    this.socket.on(RT.INCIDENT_HANDLER_CHANGED, (data) => {
      this.emit(RT_LOCAL.INCIDENT_HANDLER_CHANGED, data)
    })
    // Invalidación mínima de audiencia previa: solo remover/cerrar localmente; no refetchear el
    // subject (respondería 403/404) y no tratarlo como error.
    this.socket.on(RT.INCIDENT_ACCESS_REVOKED, (data) => {
      this.emit(RT_LOCAL.INCIDENT_ACCESS_REVOKED, data)
    })

    this.socket.on(channels.REALTIME_NOTIFICATIONS, (data) => {
      this.dispatchOrBatch(RT_LOCAL.NOTIFICATION_RECEIVED, data)
    })

    // Eventos del sistema
    // 🔐 Forzar cierre de sesión desde el backend (e.g., reset de contraseña)
    this.socket.on(RT.AUTH_FORCE_LOGOUT, (payload: any) => {
      const reason = payload?.reason || 'Tu sesión ha sido cerrada por seguridad.'
      console.warn('🔐 auth:force_logout recibido:', payload)
      try {
        NotificationService.warning({ message: reason, position: 'top-center', duration: 4000 })
      } catch {}
      try {
        const authStore = useAuthStore()
        // P12-TEST1A-R3: el servidor ya revocó la sesión. Ejecutar SOLO el teardown local
        // canónico del auth-store (sin POST /auth/logout: elimina el segundo request que la
        // sesión revocada respondería con 401). handleServerForcedLogout ya desconecta el
        // WebSocket vía clearSessionState, por lo que aquí no hace falta un disconnect() extra.
        authStore.handleServerForcedLogout()
        goToLogin()
      } catch (e) {
        // Fallback duro: garantizar desconexión y redirección aunque el store falle.
        try { this.disconnect() } catch {}
        goToLogin()
      }
    })

    // 📊 Analytics (6.1) - respuestas a solicitudes
    this.socket.on(RT.ANALYTICS_METRICS, (data) => {
      this.emit(RT_LOCAL.ANALYTICS_METRICS, data)
    })

    this.socket.on(RT.ANALYTICS_TIMESERIES, (payload) => {
      this.emit(RT_LOCAL.ANALYTICS_TIMESERIES, payload)
    })

    // 📡 NUEVO: Inventario - Actualización genérica (QC, cuarentena, defectos)
    // Inventario: actualización de solicitud (broadcast backend)
    // Inventario: cambio en inventario central (stock disponible / reservado)
    // ✅ FIX: Inventario enterprise - Cambios de stock por órdenes salida (OUTBOUND)
    // Inventario: nueva orden de salida creada
    // Inventario: actualización de orden de salida (estado)
    // ============================================================================
    // � COMENTARIOS: Emitidos a rooms por el backend (io.to('incident:N'))
    // ============================================================================
    this.socket.on(RT.COMMENT_CREATED, (data) => { this.emit(RT_LOCAL.COMMENT_CREATED, data) })
    this.socket.on(RT.COMMENT_UPDATED, (data) => { this.emit(RT_LOCAL.COMMENT_UPDATED, data) })
    this.socket.on(RT.COMMENT_DELETED, (data) => { this.emit(RT_LOCAL.COMMENT_DELETED, data) })

    // ============================================================================
    // �📅 AGENDA: Eventos de cambios en agenda (correcciones, actualizaciones)
    // ============================================================================
    // === Nuevos eventos inventario extendidos ===
    // Alertas de stock: nueva alerta    // Alertas de stock: alerta resuelta    // Transferencias: creada    // Transferencias: actualizada (estado / items)    // Solicitudes: aprobada    // Solicitudes: cancelada    // ✅ FIX: Solicitudes: creada (nueva solicitud)
    // === Eventos de Rutas ===
    // Rutas: cambio general (creación, actualización, eliminación)
    // Operadores de Rutas: cambio de asignación (titular, sustituto, restauración)
    // Ejecuciones de Rutas: cambios (programación, cancelación, reasignación)
  }

  // 🌊 Control de caudal y batching
  private dispatchOrBatch(event: string, data: any): void {
    // P13-V2B (PD-V2-04): la lista de "eventos críticos" de inventario fue PURGADA — los 12
    // nombres RT_LOCAL.INVENTORY_* eran huérfanos (cero productores tras el retiro K4A y cero
    // suscriptores tras V2B). La señal de inventario es la notificación durable K4B por el canal
    // genérico `realtime:notification`, que sigue el batching normal de notificaciones.

    // 🌊 Eventos con throttling normal (dashboard, notificaciones, estadísticas, etc.)
    if (this.paused) {
      // Acumular mientras la pestaña no está visible
      this.pendingBatch[event] = this.pendingBatch[event] || []
      this.pendingBatch[event].push(data)
      return
    }
    // Throttle simple por ventana
    this.pendingBatch[event] = this.pendingBatch[event] || []
    this.pendingBatch[event].push(data)
    if (!this.batchTimer) {
      this.batchTimer = setTimeout(() => {
        const batch = this.pendingBatch
        this.pendingBatch = {}
        this.batchTimer = null
        Object.entries(batch).forEach(([evt, items]) => {
          if (items.length === 1) {
            this.emit(evt, items[0])
          } else {
            // Emitir cada item para mantener compatibilidad con handlers existentes
            items.forEach((it) => this.emit(evt, it))
          }
        })
      }, this.throttleWindowMs)
    }
  }

  // 💤 Pausar cuando la pestaña no está visible para reducir trabajo
  constructor() {
    // @ts-ignore
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', () => {
        this.paused = document.visibilityState === 'hidden'
        if (!this.paused) {
          // Al volver a primer plano, drenar lote pendiente inmediatamente
          if (this.batchTimer) clearTimeout(this.batchTimer)
          const batch = this.pendingBatch
          this.pendingBatch = {}
          Object.entries(batch).forEach(([evt, items]) => {
            if (items.length === 1) {
              this.emit(evt, items[0])
            } else if (items.length > 1) {
              items.forEach((it) => this.emit(evt, it))
            }
          })
        }
      })
    }
  }

  // � Room Management (backward compat — PR-18)


  // �📡 Suscripciones a Canales


  // ── P13-K4A · RETIRED CLIENT PRIMITIVES ───────────────────────────────────────────────────────
  // `joinRoom` / `leaveRoom` / `subscribe` / `unsubscribe` / `publish` are GONE. Under the ratified
  // contract no room is client-selectable: the server auto-joins `user:{id}` from the PERSISTED
  // handshake identity, and every protected delivery resolves its recipients from current database
  // read authority. A client primitive that named a room or a channel could only ever have been a
  // request the server was obliged to refuse, so the honest shape is not to offer one.

  // 📊 6.1: Solicitudes de analytics por socket
  requestAnalyticsMetrics(pattern?: string): void {
    if (!this.isConnected) {
      this.queueMessage(RT_CLIENT.ANALYTICS_GET_METRICS, { pattern })
      return
    }
    this.socket?.emit(RT_CLIENT.ANALYTICS_GET_METRICS, { pattern })
  }

  requestAnalyticsTimeSeries(metric: string, duration: string = '1h'): void {
    if (!this.isConnected) {
      this.queueMessage(RT_CLIENT.ANALYTICS_GET_TIMESERIES, { metric, duration })
      return
    }
    this.socket?.emit(RT_CLIENT.ANALYTICS_GET_TIMESERIES, { metric, duration })
  }


  // 📤 Publicar Eventos

  // 🎧 Event Listeners Locales
  on(event: string, callback: WebSocketEventCallback): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, [])
    }
    this.eventListeners.get(event)!.push(callback)
  }

  off(event: string, callback?: WebSocketEventCallback): void {
    if (!this.eventListeners.has(event)) return

    if (callback) {
      const listeners = this.eventListeners.get(event)!
      const index = listeners.indexOf(callback)
      if (index !== -1) {
        listeners.splice(index, 1)
      }
    } else {
      this.eventListeners.delete(event)
    }
  }

  private emit(event: string, data: any): void {
    const listeners = this.eventListeners.get(event) || []
    listeners.forEach(callback => {
      try {
        callback(data)
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error)
      }
    })
  }

  // Método público SOLO para entorno de pruebas: permite simular eventos locales
  // sin depender del socket real.
  public emitLocal(event: string, data: any): void {
    this.emit(event, data)
  }

  // 📦 Cola de Mensajes
  private queueMessage(event: string, data: any): void {
    this.messageQueue.push({ event, data })
  console.debug(`📦 Queued message: ${event}`)
  }

  private processMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const { event, data } = this.messageQueue.shift()!
      
      // P13-K4A: the channel subscribe/publish replay branches are gone with their primitives.
      // Only catalogued client-to-server events can ever be queued now.
      this.socket?.emit(event, data)
    }
  console.debug('📦✅ Message queue processed')
  }

  // � Utilidades
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect()
      // P13-V1D R1 (F5): teardown limpio — la instancia descartada no conserva listeners del
      // Socket ni del Manager (una instancia nueva registra los suyos desde cero en connect()).
      this.socket.removeAllListeners?.()
      this.socket.io?.removeAllListeners?.()
      this.socket = null
    }
    this.isConnected = false
    this.isConnecting = false
    this.subscriptions.clear()
    // P13-K4A: the local listener bus is NOT cleared here. Clearing it on every logout orphaned
    // every store that had registered at module scope — their `_attached` guards stayed true, so
    // after a re-login nothing ever re-registered and realtime silently died for the next session.
    // Listeners belong to their owners; the socket's lifecycle is not theirs to end.
    this.messageQueue = []
    this.connectionPromise = null
    // Room bookkeeping is per-socket: a fresh socket must re-derive it, never inherit a dedupe key.
    this.lastUserSubscribeKey = null
    this.lastUserSubscribeAt = 0
  console.debug('🔌 WebSocket disconnected manually')
  }

  getConnectionStatus(): {
    connected: boolean
    connecting: boolean
    subscriptions: string[]
    queuedMessages: number
  } {
    return {
      connected: this.isConnected,
      connecting: this.isConnecting,
      subscriptions: Array.from(this.subscriptions),
      queuedMessages: this.messageQueue.length
    }
  }

  // PR-08: Auto-suscripción basada en usuario — solo rooms oficiales
  private lastUserSubscribeKey: string | null = null
  private lastUserSubscribeAt: number = 0

  // PR-08/PR-24: Mapa de roles → snake_case canónico para rooms de rol
  // Acepta tanto snake_case (canónico post-PR-23) como PascalCase (borde legacy)
  private static readonly ROLE_TO_CANONICAL: Record<string, string> = {
    // snake_case canónico (identidad)
    'super_admin': 'super_admin',
    'group_manager': 'group_manager',
    'administration_manager': 'administration_manager',
    'field_supervisor': 'field_supervisor',
    'department_head': 'department_head',
    'route_operator': 'route_operator',
    'agency_employee': 'agency_employee',
    // PascalCase legacy (borde de compatibilidad)
    'SuperAdmin': 'super_admin',
    'GroupManager': 'group_manager',
    'AdministrationManager': 'administration_manager',
    'FieldSupervisor': 'field_supervisor',
    'DepartmentHead': 'department_head',
    'RouteOperator': 'route_operator',
    'AgencyEmployee': 'agency_employee'
  }

  private static readonly LEGACY_NON_ACTIVE_ROLE_ALIASES: Record<
    string,
    { classification: 'legacy_non_active'; legacyRole: string }
  > = {
    'data_collector': {
      classification: 'legacy_non_active',
      legacyRole: 'data_collector'
    },
    'DataCollector': {
      classification: 'legacy_non_active',
      legacyRole: 'data_collector'
    }
  }

  /**
   * P13-K4A · the client no longer proposes rooms.
   *
   * The historical implementation built `user:` / `role:` / `group:` / `administration:` /
   * `department:` names from the session object and asked the server to grant them. Two things were
   * wrong with that. First, the server auto-joins `user:{id}` from the PERSISTED handshake identity,
   * so the request was redundant for the one room that exists and refused for every other. Second —
   * and this is why it mattered — three of those names were built from `user.group_id`,
   * `user.administration_id` and `user.department_id`, fields the session payload has never
   * contained, so the request silently asked for nothing while looking like it asked for something.
   *
   * Kept as a no-op rather than deleted because it has live callers in the auth store; making it
   * explicitly do nothing is clearer at the call sites than removing the concept mid-flight.
   */
  subscribeUserChannels(_user: any): void {
    void _user
  }

  /**
   * Ask the server about a room set. Retained ONLY as an observable denial probe — the server
   * answers `subscribe:rooms:result` with an empty `joined` and a per-room reason. Nothing here
   * grants anything; it exists so a denial is something the client can see rather than infer.
   */
  probeRooms(rooms: string[]): void {
    if (!this.isConnected || rooms.length === 0) return
    this.socket?.emit(RT_CLIENT.SUBSCRIBE_ROOMS, { rooms })
  }

}

// Singleton instance
export const websocketService = new WebSocketService()
export default websocketService
